import { useState, useEffect, useCallback } from "react";
import { Modal } from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';
import { ReactComponent as CloseIcon } from "../images/icons/white-close.svg";
import { getApiBaseUrl, getModels } from "../api-helper";
import axios from 'axios';
import eventBus from '../utils/eventBus';

// Placeholder image for cosyflows without example_urls
const PlaceholderImg = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect fill='%23190A4E' width='400' height='300'/%3E%3Ctext fill='%2300D9FF' font-family='Arial' font-size='24' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3ENo Preview%3C/text%3E%3C/svg%3E";

function CosyflowCard({ cosyflow, onSelect, onImageClick }) {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Get all example images or use placeholder
    const exampleImages = cosyflow.metadata?.example_urls || [PlaceholderImg];
    const hasExamples = cosyflow.metadata?.example_urls && cosyflow.metadata.example_urls.length > 0;
    const hasMultipleImages = exampleImages.length > 1;

    const currentImageUrl = exampleImages[currentImageIndex];

    const handlePrevImage = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev - 1 + exampleImages.length) % exampleImages.length);
    };

    const handleNextImage = (e) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev + 1) % exampleImages.length);
    };

    return (
        <div className="group relative bg-dark-blue/30 rounded-xl overflow-hidden border border-white/20 hover:border-yellow transition-all duration-300 h-full flex flex-col">
            {/* Cosyflow Image Slider */}
            <div className="relative overflow-hidden aspect-video flex-shrink-0 group/image">
                <img
                    src={currentImageUrl}
                    alt={cosyflow.title}
                    className="w-full h-full object-cover cursor-pointer transition-opacity hover:opacity-75"
                    onClick={(e) => {
                        e.stopPropagation();
                        hasExamples && onImageClick(cosyflow);
                    }}
                />

                {/* Image Navigation Arrows - show if multiple images */}
                {hasMultipleImages && (
                    <>
                        <button
                            onClick={handlePrevImage}
                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-all z-10 opacity-0 group-hover/image:opacity-100"
                            aria-label="Previous image"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button
                            onClick={handleNextImage}
                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-all z-10 opacity-0 group-hover/image:opacity-100"
                            aria-label="Next image"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>

                        {/* Image Counter */}
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white px-2 py-1 rounded-full text-xs font-semibold">
                            {currentImageIndex + 1} / {exampleImages.length}
                        </div>
                    </>
                )}

                {/* Gallery Hover Icon - only show if has examples */}
                {hasExamples && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/image:bg-black/40 transition-all duration-300 opacity-0 group-hover/image:opacity-100 pointer-events-none">
                        <div className="text-white text-center">
                            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-sm font-semibold">View All Examples</p>
                        </div>
                    </div>
                )}
                {/* Category Badge */}
                {cosyflow.category && (
                    <div className="absolute top-3 right-3 bg-yellow text-dark-blue px-3 py-1 rounded-full text-xs font-bold z-10">
                        {cosyflow.category}
                    </div>
                )}
            </div>

            {/* Cosyflow Info */}
            <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-2xl font-bold text-white mb-4">{cosyflow.title}</h3>
                <p className="text-lg text-white/90 mb-6 leading-relaxed flex-grow">
                    {cosyflow.description || 'AI workflow for generating content'}
                </p>

                {/* Use Cosyflow Button */}
                <button
                    onClick={() => onSelect(cosyflow)}
                    className="w-full font-bold py-4 px-5 rounded-lg transition-all duration-200 text-lg mt-auto bg-yellow hover:bg-yellow/90 transform hover:scale-105 text-dark-blue"
                >
                    Use This Workflow
                </button>
            </div>

            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-dark-blue/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
        </div>
    );
}

export default function CosyflowsGallery({ isOpen, onClose, onSelectCosyflow }) {
    const [cosyflows, setCosyflows] = useState([]);
    const [categories, setCategories] = useState(['All']);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    // Gallery modal state
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [galleryFlow, setGalleryFlow] = useState(null);
    const [galleryImages, setGalleryImages] = useState([]);
    const [currentGalleryImageIndex, setCurrentGalleryImageIndex] = useState(0);

    // Fetch cosyflows only when modal opens
    useEffect(() => {
        if (isOpen && cosyflows.length === 0) {
            console.log('[CosyflowsGallery] Modal opened, fetching cosyflows...');
            fetchCosyflows();
        }
    }, [isOpen]);

    const fetchCosyflows = async () => {
        try {
            setIsLoading(true);
            const apiBase = await getApiBaseUrl();

            console.log('[CosyflowsGallery] Fetching available models and cosyflows...');

            // Fetch both available models and cosyflows concurrently for better performance
            const [modelsResponse, cosyflowsResponse] = await Promise.all([
                // Call getModels to get available generation models
                getModels({ host: apiBase }, { type: () => {} }), // Mock dispatch since we only need the return value
                // Fetch all cosyflows from API
                axios.get(`${apiBase}/api/cosyflows/query`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${process.env.REACT_APP_API_TOKEN}`
                    }
                })
            ]);

            // Extract available model names from the models response
            const availableModelNames = modelsResponse?.models || [];
            console.log('[CosyflowsGallery] Available models for generation:', availableModelNames.length, 'models');

            if (cosyflowsResponse.data.cosyflows && cosyflowsResponse.data.cosyflows.length > 0) {
                console.log('[CosyflowsGallery] Raw cosyflows from API:', cosyflowsResponse.data.cosyflows.length);

                // Parse and transform cosyflows data
                const transformedFlows = cosyflowsResponse.data.cosyflows
                    .map(flow => {
                        let cosyflowData = {};
                        try {
                            cosyflowData = typeof flow.cosyflow === 'string'
                                ? JSON.parse(flow.cosyflow)
                                : flow.cosyflow;
                        } catch (err) {
                            console.error('Error parsing cosyflow:', err);
                        }

                        return {
                            id: flow.id,
                            title: cosyflowData.title || flow.title || 'Untitled Workflow',
                            description: cosyflowData.description || '',
                            category: cosyflowData.category || 'General',
                            metadata: cosyflowData.metadata || {},
                            cosyflowData: cosyflowData
                        };
                    })
                    // Filter to only show cosyflows for models we can actually generate from
                    .filter(flow => {
                        const canGenerate = availableModelNames.includes(flow.title);
                        if (!canGenerate) {
                            console.log(`[CosyflowsGallery] Filtering out ${flow.title} - not in available models`);
                        }
                        return canGenerate;
                    });

                console.log('[CosyflowsGallery] Filtered to available models:', transformedFlows.length, 'flows');

                // Extract unique categories from filtered flows
                const uniqueCategories = ['All', ...new Set(
                    transformedFlows
                        .map(f => f.category)
                        .filter(Boolean)
                )];

                setCosyflows(transformedFlows);
                setCategories(uniqueCategories);
                setFetchError(null);
            } else {
                setFetchError('No cosyflows available');
            }
        } catch (error) {
            console.error('Failed to fetch cosyflows or models:', error);

            // Provide more specific error messaging
            if (error.message.includes('getModels')) {
                setFetchError('Failed to load available models. Please check your connection.');
            } else {
                setFetchError('Failed to load workflows. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Filter cosyflows by category and search query
    const filteredCosyflows = cosyflows.filter(flow => {
        // Category filter
        const matchesCategory = selectedCategory === 'All' || flow.category === selectedCategory;

        // Search filter
        const matchesSearch = searchQuery === '' ||
            flow.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (flow.description && flow.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (flow.category && flow.category.toLowerCase().includes(searchQuery.toLowerCase()));

        return matchesCategory && matchesSearch;
    });

    const handleCosyflowSelect = (cosyflow) => {
        console.log('[CosyflowsGallery] Selected cosyflow:', cosyflow.title);

        // Emit event to select model in PromptHeader
        eventBus.emit('selectModel', {
            modelName: cosyflow.title,
            openSettings: true
        });

        // Call parent callback if provided
        if (onSelectCosyflow) {
            onSelectCosyflow(cosyflow);
        }

        // Close the gallery
        onClose();
    };

    const handleImageClick = useCallback((cosyflow) => {
        console.log('[Gallery] Opening gallery for cosyflow:', cosyflow.title);
        setGalleryFlow(cosyflow);
        setCurrentGalleryImageIndex(0);

        // Get example images from metadata
        const exampleUrls = cosyflow.metadata?.example_urls || [];
        const images = exampleUrls.map((url, index) => ({
            url,
            description: `Example ${index + 1}`,
            model: cosyflow.title
        }));

        setGalleryImages(images);
        setIsGalleryOpen(true);
    }, []);

    // Don't render anything if modal is not open
    if (!isOpen) {
        return null;
    }

    return (
        <>
            {/* Main Gallery Modal */}
            <Modal
                open={isOpen}
                onClose={onClose}
                center
                closeIcon={<CloseIcon className="fill-white" />}
                classNames={{
                    modal: 'customModal',
                    overlay: 'customOverlay'
                }}
                styles={{
                    modal: {
                        background: '#190A4E',
                        border: '2px solid rgba(0, 217, 255, 0.5)',
                        borderRadius: '12px',
                        padding: '2rem',
                        maxWidth: '90vw',
                        width: '90%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        zIndex: 99999999
                    },
                    overlay: {
                        background: 'rgba(0, 0, 0, 0.8)',
                        zIndex: 99999998
                    },
                    closeButton: {
                        width: '24px',
                        height: '24px',
                        top: '12px',
                        right: '12px',
                        zIndex: 99999999
                    }
                }}
            >
                <div className="relative">
                    <h2 className="text-xl py-2 font-bold flex flex-row items-center text-white">
                        <span>AI Workflows Gallery</span>
                        <hr className="ml-2 flex-grow border border-white/40 bg-white/40" />
                    </h2>

                    {/* Error message */}
                    {fetchError && (
                        <div className="mt-4 bg-yellow/20 border border-yellow/50 rounded-lg p-3 text-white">
                            <p className="text-sm">{fetchError}</p>
                        </div>
                    )}

                    {/* Loading state */}
                    {isLoading && (
                        <div className="mt-8 text-center">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-yellow"></div>
                            <p className="text-white mt-4">Loading workflows...</p>
                        </div>
                    )}

                    {/* Search Bar */}
                    {!isLoading && (
                        <div className="mt-4 mb-4">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search workflows by name, description, or category..."
                                className="w-full bg-dark-blue/30 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-cyan focus:outline-none text-base"
                            />
                        </div>
                    )}

                    {/* Category Filter */}
                    {!isLoading && (
                        <div className="flex flex-wrap gap-2 mb-6">
                            {categories.map(category => (
                                <button
                                    key={category}
                                    onClick={() => setSelectedCategory(category)}
                                    className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                                        selectedCategory === category
                                            ? 'bg-yellow text-dark-blue'
                                            : 'bg-dark-blue/30 text-white hover:bg-dark-blue/50 border border-white/20'
                                    }`}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Cosyflows Grid */}
                    {!isLoading && filteredCosyflows.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredCosyflows.map(cosyflow => (
                                <CosyflowCard
                                    key={cosyflow.id}
                                    cosyflow={cosyflow}
                                    onSelect={handleCosyflowSelect}
                                    onImageClick={handleImageClick}
                                />
                            ))}
                        </div>
                    ) : !isLoading ? (
                        <div className="text-center py-12">
                            <p className="text-white/60 text-lg">No workflows found matching your search.</p>
                            <button
                                onClick={() => {
                                    setSearchQuery('');
                                    setSelectedCategory('All');
                                }}
                                className="mt-4 bg-yellow text-dark-blue px-6 py-2 rounded-lg font-semibold hover:bg-yellow/90 transition-all"
                            >
                                Clear Filters
                            </button>
                        </div>
                    ) : null}
                </div>
            </Modal>

            {/* Gallery Examples Modal */}
            <Modal
                open={isGalleryOpen}
                onClose={() => {
                    setIsGalleryOpen(false);
                    setGalleryFlow(null);
                    setGalleryImages([]);
                    setCurrentGalleryImageIndex(0);
                }}
                center
                closeIcon={<CloseIcon className="fill-white" />}
                classNames={{
                    modal: 'customModal',
                    overlay: 'customOverlay'
                }}
                styles={{
                    modal: {
                        background: '#190A4E',
                        border: '2px solid rgba(0, 217, 255, 0.5)',
                        borderRadius: '12px',
                        padding: 0,
                        maxWidth: '72rem',
                        width: '95%',
                        maxHeight: '90vh'
                    },
                    overlay: {
                        background: 'rgba(0, 0, 0, 0.9)'
                    },
                    closeButton: {
                        width: '24px',
                        height: '24px',
                        top: '12px',
                        right: '12px',
                        zIndex: 10
                    }
                }}
            >
                {galleryFlow && galleryImages.length > 0 ? (
                    <div className="p-6">
                        {/* Gallery Header */}
                        <div className="mb-6">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-grow">
                                    <h2 className="text-2xl font-bold text-white mb-2">{galleryFlow.title}</h2>
                                    {galleryFlow.category && (
                                        <div className="inline-flex items-center gap-2 bg-yellow/20 border border-yellow/40 rounded-full px-3 py-1 mb-3">
                                            <svg className="w-4 h-4 text-yellow" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                                            </svg>
                                            <span className="text-yellow font-semibold text-sm">{galleryFlow.category}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {galleryFlow.description && (
                                <p className="text-white/80 text-base leading-relaxed">{galleryFlow.description}</p>
                            )}
                        </div>

                        {/* Horizontal Scrolling Images Gallery */}
                        <div className="mb-4">
                            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-cyan/50 scrollbar-track-dark-blue/30">
                                {galleryImages.map((image, index) => (
                                    <div
                                        key={index}
                                        className="flex-shrink-0 snap-center group"
                                    >
                                        <div className="relative rounded-lg overflow-hidden border-2 border-white/20 hover:border-cyan/50 transition-all duration-200">
                                            <img
                                                src={image.url}
                                                alt={image.description}
                                                className="w-64 h-48 object-cover"
                                            />
                                            {/* Image number badge */}
                                            <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded-full text-xs font-semibold">
                                                {index + 1}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setIsGalleryOpen(false);
                                    setGalleryFlow(null);
                                    setGalleryImages([]);
                                    setCurrentGalleryImageIndex(0);
                                }}
                                className="flex-1 bg-dark-blue/50 hover:bg-dark-blue/70 text-white font-bold py-3 px-4 rounded-lg transition-all border border-white/20"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    handleCosyflowSelect(galleryFlow);
                                    setIsGalleryOpen(false);
                                    setGalleryFlow(null);
                                    setGalleryImages([]);
                                    setCurrentGalleryImageIndex(0);
                                }}
                                className="flex-1 bg-yellow hover:bg-yellow/90 text-dark-blue font-bold py-3 px-4 rounded-lg transition-all"
                            >
                                Use This Workflow
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-screen">
                        <div className="text-center">
                            <p className="text-white text-lg">No example images available</p>
                            <button
                                onClick={() => {
                                    setIsGalleryOpen(false);
                                    setGalleryFlow(null);
                                    setGalleryImages([]);
                                    setCurrentGalleryImageIndex(0);
                                }}
                                className="mt-4 bg-yellow hover:bg-yellow/90 text-dark-blue font-bold py-2 px-6 rounded-lg transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
}